import Comment from '@/models/Comment';
import LabourDailyRecord from '@/models/LabourDailyRecord';
import LabourDailyRecordService from '@/services/LabourDailyRecordService';
import { defineStore } from 'pinia';
import { useRepo } from 'pinia-orm';

export const useLabourDailyRecordStore = defineStore('labourDailyRecords', {
  state: () => ({}),

  getters: {
    models: () => useRepo(LabourDailyRecord),
  },

  actions: {
    async createRecord(labourId: Id, recordForm: ApiRequests['api.v1.labour.daily.records.store']) {
      const [data, error] = await LabourDailyRecordService.create(labourId, recordForm);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async updateRecord(
      id: Id,
      recordForm: ApiRequests['api.v1.labour.daily.records.update'],
      transformData: { day: string },
    ) {
      const [data, error] = await LabourDailyRecordService.update(id, recordForm, transformData);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async approve(id: Id) {
      const [data, error] = await LabourDailyRecordService.approve(id);

      if(error) {
        throw error;
      }

      this.models.save(data.data);

      return data;
    },

    async unapprove(id: Id) {
      const [data, error] = await LabourDailyRecordService.unapprove(id);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async paid(id: Id, paid: ApiRequests['api.v1.labour.daily.records.paid']) {
      const [data, error] = await LabourDailyRecordService.paid(id, paid);

      if(error) {
        throw error;
      }

      this.models.save(data.data);

      return data;
    },

    async fetchRecords(
      labourId: Id,
      form: ApiRequests['api.v1.labour.daily.records.index'] = {},
    ): Promise<CursorPaginator<LabourDailyRecord> & { has_more: boolean }> {
      const [data, error] = await LabourDailyRecordService.index(labourId, form);

      if(error) {
        throw error;
      }

      this.models.save(data.data);

      return data as unknown as CursorPaginator<LabourDailyRecord> & { has_more: boolean };
    },

    async fetchRecord(recordId: Id) {
      const [data, error] = await LabourDailyRecordService.show(recordId);

      if(error) {
        throw error;
      }

      // Save comments separately as pinia orm cannot handle saving the morph relationship
      const comments = data.comments;

      delete data.comments;
      const commentsRepo = useRepo(Comment);

      commentsRepo.save(comments);

      this.models.save(data);

      return data;
    },

    async splitRecord(
      id: Id,
      request: ApiRequests['api.v1.labour.daily.records.split'],
      transformData: { newDay: string; oldDay: string },
    ) {
      const [data, error] = await LabourDailyRecordService.split(id, request, transformData);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async comment(id: Id, request: ApiRequests['api.v1.labour.daily.records.comments']) {
      const [data, error] = await LabourDailyRecordService.comment(id, request);

      if(error) {
        throw error;
      }

      const comments = useRepo(Comment);

      comments.save(data);

      return data;
    },

    async vendor(id: Id, form: ApiRequests['api.v1.labour.daily.records.vendor']) {
      const [data, error] = await LabourDailyRecordService.vendor(id, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async search(projectId: Id, form: ApiRequests['api.v1.labour.daily.records.search']) {
      const [data, error] = await LabourDailyRecordService.search(projectId, form);

      if(error) {
        throw error;
      }

      return data;
    },

    async deleteRecord(id: Id) {
      const [data, error] = await LabourDailyRecordService.delete(id);

      if(error) {
        throw error;
      }

      this.models.destroy(data.id);

      return data;
    },

    async fetchSignedRecords(
      claimId: Id,
      signedParams: unknown,
      labourId: Id,
      page = 1,
    ) {
      const [data, error] = await LabourDailyRecordService.signed.records(
        claimId,
        signedParams,
        {
          labour_id: Number(labourId),
          page,
        },
      );

      if(error) {
        throw error;
      }

      this.models.save(data.data);

      return data;
    },
  },
});
